/**
 * Redux async actions (src/store/actions/counsellorActions.js).
 *
 * This module defines Redux Toolkit thunks (createAsyncThunk) that call backend endpoints via the shared axios client.
 * Slices consume the fulfilled payloads in `extraReducers` to update the UI state.
 */

import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../helpers/axiosconfig";
import { toAdminRequestError } from "../../utils/adminRequestError";
import { setAuthStorage } from "../../helpers/authStorage";

export const isCounsellorIdentity = (value) => {
  const role = String(value?.UserType || value?.userType || value?.role || '')
    .trim()
    .toLowerCase();
  return role === 'counsellor' || role === 'counselor';
};

const extractCounsellor = (payload) => (
  payload?.counsellor || payload?.user || payload?.data?.counsellor || payload?.data?.user || payload?.data || null
);

// Counsellor Login
/**
 * Async thunk: loginCounsellor.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const loginCounsellor = createAsyncThunk(
  "counsellor/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const { rememberMe = true, ...payload } = credentials || {};
      const response = await axios.post("v1/counsellor/login", payload);
      const counsellor = extractCounsellor(response.data);

      if (!isCounsellorIdentity(counsellor)) {
        return rejectWithValue("The account is not authorized for the counsellor panel");
      }

      if (response.data.token && counsellor?._id) {
        setAuthStorage({
          token: response.data.token,
          userId: counsellor._id,
          userData: {
            _id: counsellor._id,
            Username: counsellor.Username,
            UserType: counsellor.UserType,
            Email: counsellor.Email
          },
          rememberMe
        });
      }

      return counsellor;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Login failed"
      );
    }
  }
);

// Get Counsellor Profile
/**
 * Async thunk: getCounsellorProfile.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getCounsellorProfile = createAsyncThunk(
  "counsellor/getProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("v1/counsellor/profile");
      const counsellor = extractCounsellor(response.data);
      if (!isCounsellorIdentity(counsellor)) {
        return rejectWithValue("The account is not authorized for the counsellor panel");
      }
      return counsellor;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch profile"
      );
    }
  }
);

// Update Counsellor Profile
/**
 * Async thunk: updateCounsellorProfile.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const updateCounsellorProfile = createAsyncThunk(
  "counsellor/updateProfile",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.put("v1/counsellor/profile", data);
      return response.data.counsellor;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update profile"
      );
    }
  }
);

// Change Counsellor Password
/**
 * Async thunk: changeCounsellorPassword.
 *
 * Sends only the old and new password fields accepted by the counsellor API.
 */
export const changeCounsellorPassword = createAsyncThunk(
  "counsellor/changePassword",
  async ({ oldPassword, newPassword }, { rejectWithValue }) => {
    try {
      const response = await axios.put("v1/counsellor/change-password", {
        oldPassword,
        newPassword,
      });
      // sendToken refreshes the httpOnly session cookie; never persist its
      // boolean compatibility flag as a client-readable bearer token.
      const counsellor = extractCounsellor(response.data);
      if (!isCounsellorIdentity(counsellor) || !counsellor?._id) {
        return rejectWithValue({ status: 200, code: "PASSWORD_CHANGE_FAILED" });
      }
      return { counsellor };
    } catch (error) {
      const code = error.response?.data?.code;
      return rejectWithValue({
        status: Number(error.response?.status) || 0,
        code: ["INVALID_CURRENT_PASSWORD", "INVALID_PASSWORD_INPUT", "PASSWORD_CHANGE_CONFLICT"].includes(code)
          ? code : "PASSWORD_CHANGE_FAILED",
      });
    }
  }
);

// Logout Counsellor
/**
 * Async thunk: logoutCounsellor.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const logoutCounsellor = createAsyncThunk(
  "counsellor/logout",
  async (_, { rejectWithValue }) => {
    try {
      await axios.post("v1/counsellor/logout");
      try {
        // BUG-004/005: Token is in httpOnly cookie (cleared by backend on logout).
        // Only clear non-sensitive display data from storage.
        localStorage.removeItem('userId');
        localStorage.removeItem('userData');
        sessionStorage.removeItem('userId');
        sessionStorage.removeItem('userData');
      } catch (_) {
        // Ignore storage access errors during logout cleanup.
      }
      return null;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Logout failed"
      );
    }
  }
);

// Create Invite (Counsellor)
/**
 * Async thunk: createInvite.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const createInvite = createAsyncThunk(
  "counsellor/createInvite",
  async (inviteData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "v1/counsellor/create-invite",
        inviteData
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create invite"
      );
    }
  }
);

// Get Invites List (Counsellor)
/**
 * Async thunk: getInvitesList.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getInvitesList = createAsyncThunk(
  "counsellor/getInvitesList",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.get("v1/counsellor/invites-list", {
        params,
      });
      const payload = response.data.data;
      return {
        invites: Array.isArray(payload?.invites)
          ? payload.invites
          : Array.isArray(payload)
            ? payload
            : [],
        summary: payload?.summary || null,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch invites"
      );
    }
  }
);

// Admin: Get All Counsellors
/**
 * Async thunk: getAllCounsellors.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getAllCounsellors = createAsyncThunk(
  "counsellor/getAllCounsellors",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("admin/counsellors");
      return response.data.counsellors;
    } catch (error) {
      return rejectWithValue(
        toAdminRequestError(error, "Failed to fetch counsellors")
      );
    }
  }
);

// Admin: Create Counsellor
/**
 * Async thunk: createCounsellorByAdmin.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const createCounsellorByAdmin = createAsyncThunk(
  "counsellor/createByAdmin",
  async (counsellorData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "admin/counsellor/create",
        counsellorData
      );
      return response.data.counsellor;
    } catch (error) {
      return rejectWithValue(
        toAdminRequestError(error, "Failed to create counsellor")
      );
    }
  }
);

// Admin: Update Counsellor
/**
 * Async thunk: updateCounsellorByAdmin.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const updateCounsellorByAdmin = createAsyncThunk(
  "counsellor/updateByAdmin",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`admin/counsellor/${id}`, data);
      return response.data.counsellor;
    } catch (error) {
      return rejectWithValue(
        toAdminRequestError(error, "Failed to update counsellor")
      );
    }
  }
);

// Admin: Delete Counsellor
/**
 * Async thunk: deleteCounsellorByAdmin.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const deleteCounsellorByAdmin = createAsyncThunk(
  "counsellor/deleteByAdmin",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`admin/counsellor/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        toAdminRequestError(error, "Failed to delete counsellor")
      );
    }
  }
);

// Get All Teachers (for counsellor to view)
/**
 * Async thunk: getCounsellorTeachers.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getCounsellorTeachers = createAsyncThunk(
  "counsellor/getTeachers",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.get("v1/counsellor/teachers", { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch teachers"
      );
    }
  }
);

// Get Teacher Details (for counsellor to view specific teacher)
/**
 * Async thunk: getCounsellorTeacherDetails.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getCounsellorTeacherDetails = createAsyncThunk(
  "counsellor/getTeacherDetails",
  async (teacherId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`v1/counsellor/teachers/${teacherId}`);
      return response.data.teacher;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch teacher details"
      );
    }
  }
);

// Get All Courses (for counsellor to view)
/**
 * Async thunk: getCounsellorCourses.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getCounsellorCourses = createAsyncThunk(
  "counsellor/getCourses",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.get("v1/counsellor/courses", { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch courses"
      );
    }
  }
);

// Get All Packages (for counsellor to view)
/**
 * Async thunk: getCounsellorPackages.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getCounsellorPackages = createAsyncThunk(
  "counsellor/getPackages",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.get("v1/counsellor/packages", { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch packages"
      );
    }
  }
);

// ==================== INVITE NOTES ACTIONS ====================

// Create Invite Note
/**
 * Async thunk: createCounsellorInviteNote.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const createCounsellorInviteNote = createAsyncThunk(
  "counsellorNotes/createInviteNote",
  async (noteData, { rejectWithValue }) => {
    try {
      const response = await axios.post("v1/counsellor/inviteNotes", noteData);
      return response.data.note;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create note"
      );
    }
  }
);

// Get Notes for Specific Invite
/**
 * Async thunk: getInviteNotes.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getInviteNotes = createAsyncThunk(
  "counsellorNotes/getInviteNotes",
  async ({ inviteId, params }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`v1/counsellor/invites/${inviteId}/inviteNotes`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch notes"
      );
    }
  }
);

// Get All Invite Notes for Counsellor
/**
 * Async thunk: getAllCounsellorInviteNotes.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getAllCounsellorInviteNotes = createAsyncThunk(
  "counsellorNotes/getAllInviteNotes",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.get("v1/counsellor/inviteNotes", { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch all notes"
      );
    }
  }
);

// Update Invite Note
/**
 * Async thunk: updateCounsellorInviteNote.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const updateCounsellorInviteNote = createAsyncThunk(
  "counsellorNotes/updateInviteNote",
  async ({ noteId, noteData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`v1/counsellor/inviteNotes/${noteId}`, noteData);
      return response.data.note;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update note"
      );
    }
  }
);

// Delete Invite Note
/**
 * Async thunk: deleteCounsellorInviteNote.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const deleteCounsellorInviteNote = createAsyncThunk(
  "counsellorNotes/deleteInviteNote",
  async (noteId, { rejectWithValue }) => {
    try {
      await axios.delete(`v1/counsellor/inviteNotes/${noteId}`);
      return noteId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete note"
      );
    }
  }
);

// Toggle Pin on Invite Note
/**
 * Async thunk: togglePinCounsellorInviteNote.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const togglePinCounsellorInviteNote = createAsyncThunk(
  "counsellorNotes/togglePinInviteNote",
  async (noteId, { rejectWithValue }) => {
    try {
      const response = await axios.put(`v1/counsellor/inviteNotes/${noteId}/toggle-pin`);
      return response.data.note;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to toggle pin"
      );
    }
  }
);
